/* eslint-disable unicorn/prefer-module */
import { useCallback, useEffect, useMemo, useState } from "react";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import cx from "classix";
import { Loader } from "..";
import { getEdificiosOperativos, useCategoriaMoneda } from "../../utils";
import { Header } from "../Header";
import styles from "./CategoriaInversiones.module.scss";

// Datos IPSOS Inversiones (Base: 400 casos general, 106 casos inversores)
const IPSOS_DATA = {
  realizaInversiones: 0.26, // 26%
  noRealizaInversiones: 0.74, // 74% - OPORTUNIDAD
  tiposInversion: {
    "Depósito a Plazo": 0.431,
    "Fondos Mutuos": 0.257,
    Acciones: 0.195,
    Criptomonedas: 0.176,
    Otro: 0.099,
    Bolsa: 0.053,
  },
  prioridades: {
    Rentabilidad: 0.449,
    Seguridad: 0.334,
    Liquidez: 0.218,
  },
  canales: {
    "Banca privada": 0.612,
    "Plataforma online": 0.246,
    "Corredora de bolsa": 0.097,
    Otro: 0.045,
    "Firma de asesorías": 0.042,
  },
};

// Tickets promedio de inversión (USD)
const TICKETS_INVERSION = [5000, 10000, 25000, 50000];

// Escenarios de captación
const ESCENARIOS_CAPTACION = [5, 10, 20, 30];

export const Inversiones = () => {
  const { formatNumber, formatUSD, pais } = useCategoriaMoneda();
  const [edificios, setEdificios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [checkAll, setCheckAll] = useState(true);
  const [checkedBuildings, setCheckedBuildings] = useState({});
  const [showInventarioModal, setShowInventarioModal] = useState(false);
  const [showOportunidadModal, setShowOportunidadModal] = useState(false);
  const [canalSeleccionado, setCanalSeleccionado] = useState("Banca privada");
  const [porcentajeCaptacion, setPorcentajeCaptacion] = useState(10);
  const [ticketPromedio, setTicketPromedio] = useState(10000);

  useEffect(() => {
    const initialize = async () => {
      try {
        const newEdificios = await getEdificiosOperativos({ pais });
        setEdificios(newEdificios);

        const initialChecked = {};
        newEdificios.forEach(({ id }) => {
          initialChecked[id] = true;
        });
        setCheckedBuildings(initialChecked);
      } finally {
        setLoading(false);
      }
    };

    initialize();
  }, [pais]);

  const toggleCheckAll = useCallback(() => {
    if (!checkAll) {
      const allChecked = {};
      edificios.forEach(({ id }) => {
        allChecked[id] = true;
      });
      setCheckedBuildings(allChecked);
    } else {
      setCheckedBuildings({});
    }
    setCheckAll((prev) => !prev);
  }, [checkAll, edificios]);

  const toggleCheck = useCallback((id) => {
    setCheckedBuildings((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Calcular totales de edificios seleccionados
  const { totalEjecutivos, cantidadEdificios, totalPantallas, totalEmpresas } = useMemo(() => {
    let ejecutivos = 0;
    let cantidad = 0;
    let pantallas = 0;
    let empresas = 0;

    edificios.forEach(({ id, personas, pantallas: p, espera, totems, cowork, empresaslistado }) => {
      if (checkedBuildings[id]) {
        cantidad++;
        ejecutivos += personas || 0;
        pantallas += (p || 0) + (espera || 0) + (totems || 0) + (cowork || 0);
        empresas += empresaslistado ? empresaslistado.split(",").length : 0;
      }
    });

    return {
      totalEjecutivos: ejecutivos,
      cantidadEdificios: cantidad,
      totalPantallas: pantallas,
      totalEmpresas: empresas,
    };
  }, [edificios, checkedBuildings]);

  // Calcular mercado con IPSOS
  const resultados = useMemo(() => {
    if (!totalEjecutivos) return null;

    // Inversores y no inversores
    const inversores = Math.round(totalEjecutivos * IPSOS_DATA.realizaInversiones);
    const noInversores = Math.round(totalEjecutivos * IPSOS_DATA.noRealizaInversiones);

    // Desglose por tipo de inversión (sobre los inversores)
    const tiposInversion = {};
    Object.entries(IPSOS_DATA.tiposInversion).forEach(([tipo, pct]) => {
      tiposInversion[tipo] = {
        personas: Math.round(inversores * pct),
        pct: pct * 100,
      };
    });

    // Desglose por prioridad (sobre los inversores)
    const prioridades = {};
    Object.entries(IPSOS_DATA.prioridades).forEach(([prioridad, pct]) => {
      prioridades[prioridad] = {
        personas: Math.round(inversores * pct),
        pct: pct * 100,
      };
    });

    // Desglose por canal (sobre los inversores)
    const canales = {};
    Object.entries(IPSOS_DATA.canales).forEach(([canal, pct]) => {
      canales[canal] = {
        personas: Math.round(inversores * pct),
        pct: pct * 100,
      };
    });

    // Mercado potencial (inversores * ticket promedio)
    const mercadoActual = inversores * ticketPromedio;
    const mercadoPotencialNoInversores = noInversores * ticketPromedio;

    return {
      totalEjecutivos,
      inversores,
      noInversores,
      tiposInversion,
      prioridades,
      canales,
      mercadoActual,
      mercadoPotencialNoInversores,
      mercadoTotal: mercadoActual + mercadoPotencialNoInversores,
    };
  }, [totalEjecutivos, ticketPromedio]);

  // Calcular oportunidad por canal
  const calcularOportunidadCanal = useCallback(
    (canal, porcentajeCrecimiento) => {
      if (!resultados) return { clientesGanados: 0, mercadoGanado: 0 };

      const marketShareActual = IPSOS_DATA.canales[canal] || 0;
      const nuevoMarketShare = marketShareActual * (1 + porcentajeCrecimiento / 100);

      const clientesActuales = Math.round(resultados.inversores * marketShareActual);
      const clientesNuevos = Math.round(resultados.inversores * nuevoMarketShare);
      const clientesGanados = clientesNuevos - clientesActuales;

      // Además, captar del 74% que no invierte (10% del crecimiento aplica a nuevos inversores)
      const noInversoresCaptados = Math.round(resultados.noInversores * (porcentajeCrecimiento / 100) * 0.1);

      const totalClientesGanados = clientesGanados + noInversoresCaptados;
      const mercadoGanado = totalClientesGanados * ticketPromedio;

      return {
        clientesActuales,
        clientesNuevos,
        clientesGanados,
        noInversoresCaptados,
        totalClientesGanados,
        marketShareActual: marketShareActual * 100,
        marketShareNuevo: nuevoMarketShare * 100,
        mercadoGanado,
      };
    },
    [resultados, ticketPromedio]
  );

  if (loading || edificios.length === 0) return <Loader />;

  return (
    <div className={styles.inversionesPage}>
      <Header leftContent={<h1 className={styles.headerTitulo}>Mercado Inversiones</h1>}>
        <div className={styles.inventarioHeader}>
          <div className={styles.inventarioInfo}>
            <span>
              <strong>{cantidadEdificios}</strong> edificios
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalPantallas)}</strong> pantallas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEjecutivos)}</strong> personas
            </span>
            <span className={styles.separadorVertical}>|</span>
            <span>
              <strong>{formatNumber(totalEmpresas)}</strong> empresas
            </span>
          </div>
          <button type="button" className={styles.inventarioBtn} onClick={() => setShowInventarioModal(true)}>
            Inventario
          </button>
        </div>
      </Header>

      <div className={styles.inversionesContent}>
        {/* Tabla resumen */}
        {resultados && (
          <div className={styles.tablaContainer}>
            <table className={cx("table table-sm", styles.tablaResultados)}>
              <thead className="table-dark">
                <tr>
                  <th className={cx("text-center", styles.thSmall)}>Sí invierten</th>
                  <th className={cx("text-center", styles.thSmall)}>No invierten</th>
                  <th className={cx("text-center", styles.thTicket)}>Ticket prom.</th>
                  <th className={cx("text-center", styles.thMercado)}>Mercado potencial</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className={cx("text-center align-middle", styles.celdaSi)}>
                    <div className={styles.celdaContenido}>
                      <span className={styles.badgeIpsos}>{(IPSOS_DATA.realizaInversiones * 100).toFixed(0)}%</span>
                      <div className={styles.valorGrande}>{formatNumber(resultados.inversores)}</div>
                      <div className={styles.subtexto}>inversores</div>
                    </div>
                  </td>
                  <td className={cx("text-center align-middle", styles.celdaOportunidad)}>
                    <div className={styles.celdaContenido}>
                      <span className={cx(styles.badgeIpsos, styles.badgeOportunidad)}>
                        {(IPSOS_DATA.noRealizaInversiones * 100).toFixed(0)}%
                      </span>
                      <div className={styles.valorGrande}>{formatNumber(resultados.noInversores)}</div>
                      <div className={styles.subtexto}>oportunidad de captación</div>
                    </div>
                  </td>
                  <td className="text-center align-middle">
                    <div className={styles.celdaContenido}>
                      <select
                        value={ticketPromedio}
                        onChange={(e) => setTicketPromedio(Number(e.target.value))}
                        className="form-select form-select-sm"
                        style={{ width: "130px" }}
                      >
                        {TICKETS_INVERSION.map((t) => (
                          <option key={t} value={t}>
                            {formatUSD(t)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </td>
                  <td className={cx("text-center align-middle", styles.celdaTotal)}>
                    <div className={styles.valorDestacado}>{formatUSD(resultados.mercadoTotal)}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}

        {/* Tablas de desglose: Tipos de inversión + Prioridades */}
        {resultados && (
          <div className={styles.tablasDesglose}>
            <div className={styles.desgloseContainer}>
              <h4>¿En qué invierten?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Tipo de inversión</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.tiposInversion).map(([tipo, data]) => (
                    <tr key={tipo}>
                      <td className="text-start">{tipo}</td>
                      <td className="text-center">
                        <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className={styles.desgloseContainer}>
              <h4>¿Qué priorizan al invertir?</h4>
              <table className={cx("table table-bordered table-sm", styles.tablaDesglose)}>
                <thead className="table-dark">
                  <tr>
                    <th className="text-start">Prioridad</th>
                    <th className="text-center">%</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(resultados.prioridades).map(([prioridad, data]) => (
                    <tr key={prioridad}>
                      <td className="text-start">{prioridad}</td>
                      <td className="text-center">
                        <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                      </td>
                      <td className="text-center">{formatNumber(data.personas)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Market Share por Canal */}
        {resultados && (
          <div className={styles.marketShareContainer}>
            <h4 className={styles.marketShareTitulo}>Market Share por Canal de Inversión</h4>
            <div className={styles.marketShareGrid}>
              {Object.entries(resultados.canales).map(([canal, data]) => (
                <div key={canal} className={styles.marketShareCard}>
                  <div className={styles.cardNombre}>{canal}</div>
                  <div className={styles.cardShare}>
                    <span className={styles.badgeIpsos}>{data.pct.toFixed(0)}%</span>
                  </div>
                  <div className={styles.cardMetricas}>
                    <div>
                      <span className={styles.metricaLabel}>Personas</span>
                      <span className={styles.metricaValor}>{formatNumber(data.personas)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className={styles.simularBtn} onClick={() => setShowOportunidadModal(true)}>
              Simular Oportunidad
            </button>
          </div>
        )}

        {/* Disclaimer */}
        <div className={styles.disclaimer}>
          <p>
            <strong>Fuente:</strong> IPSOS Wecast 2025. Base: 400 casos / 106 inversores. El 44.9% prioriza
            rentabilidad, 33.4% seguridad, 24.6% usa plataformas online, 17.6% invierte en criptomonedas.
          </p>
        </div>
      </div>

      {/* Modal de Inventario */}
      {showInventarioModal && (
        <div className={styles.modalOverlay} onClick={() => setShowInventarioModal(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Inventario de Edificios</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowInventarioModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.modalSummary}>
                <span>
                  <strong>{cantidadEdificios}</strong> edificios seleccionados
                </span>
                <span>
                  <strong>{formatNumber(totalEjecutivos)}</strong> personas
                </span>
                <button type="button" className={styles.toggleAllBtn} onClick={toggleCheckAll}>
                  {checkAll ? "Deseleccionar todos" : "Seleccionar todos"}
                </button>
              </div>
              <table className="table table-sm table-hover">
                <thead className="table-dark">
                  <tr>
                    <th className="text-center">✓</th>
                    <th className="text-center">Edificio</th>
                    <th className="text-center">Ubicación</th>
                    <th className="text-center">Pantallas</th>
                    <th className="text-center">Personas</th>
                  </tr>
                </thead>
                <tbody>
                  {edificios.map(
                    ({ id, edificio, ubicacion, personas, pantallas, espera, totems, cowork, created_at }) => (
                      <tr
                        key={created_at}
                        onClick={() => toggleCheck(id)}
                        className={cx(checkedBuildings[id] && "table-success")}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="text-center">
                          {checkedBuildings[id] ? <ImCheckboxChecked size={16} /> : <ImCheckboxUnchecked size={16} />}
                        </td>
                        <td className="text-center">{edificio.toUpperCase()}</td>
                        <td className="text-center">{ubicacion}</td>
                        <td className="text-center">
                          {formatNumber((pantallas || 0) + (espera || 0) + (totems || 0) + (cowork || 0))}
                        </td>
                        <td className="text-center">{personas ? formatNumber(personas) : "-"}</td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowInventarioModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Oportunidad */}
      {showOportunidadModal && (
        <div className={styles.modalOverlay} onClick={() => setShowOportunidadModal(false)}>
          <div className={cx(styles.modalContent, styles.modalOportunidad)} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>Simulador de Oportunidad por Canal</h3>
              <button type="button" className={styles.modalClose} onClick={() => setShowOportunidadModal(false)}>
                ×
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.simuladorContainer}>
                <div className={styles.simuladorControles}>
                  <div className={styles.controlGroup}>
                    <label>Canal objetivo:</label>
                    <select
                      value={canalSeleccionado}
                      onChange={(e) => setCanalSeleccionado(e.target.value)}
                      className="form-select form-select-sm"
                    >
                      {Object.keys(IPSOS_DATA.canales).map((canal) => (
                        <option key={canal} value={canal}>
                          {canal}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={styles.controlGroup}>
                    <label>% crecimiento:</label>
                    <select
                      value={porcentajeCaptacion}
                      onChange={(e) => setPorcentajeCaptacion(Number(e.target.value))}
                      className="form-select form-select-sm"
                    >
                      {ESCENARIOS_CAPTACION.map((p) => (
                        <option key={p} value={p}>
                          +{p}%
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {(() => {
                  const oportunidad = calcularOportunidadCanal(canalSeleccionado, porcentajeCaptacion);
                  return (
                    <div className={styles.simuladorResultados}>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Clientes actuales ({canalSeleccionado}):</span>
                        <span className={styles.resultadoValor}>{formatNumber(oportunidad.clientesActuales)}</span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Clientes ganados (del share):</span>
                        <span className={styles.resultadoValorDestacado}>
                          +{formatNumber(oportunidad.clientesGanados)}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Nuevos inversores captados (del 74%):</span>
                        <span className={styles.resultadoValorDestacado}>
                          +{formatNumber(oportunidad.noInversoresCaptados)}
                        </span>
                      </div>
                      <div className={styles.resultadoItem}>
                        <span className={styles.resultadoLabel}>Mercado ganado:</span>
                        <span className={styles.resultadoValorDestacado}>{formatUSD(oportunidad.mercadoGanado)}</span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button type="button" className={styles.modalBtn} onClick={() => setShowOportunidadModal(false)}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inversiones;
